// 첫 로그인 시 주문 알림 동의 모달(개선항목 4).
// 사용자가 마이페이지에서 일일이 찾아 켜지 않아도 되도록, 로그인 직후 한 번 동의 모달을 띄우고
// 동의하면 즉시 푸시 구독(권한 요청 + 서버 등록)까지 진행한다.
//
// 노출 조건(모두 만족할 때만 1회):
//  - 로그인 상태(session 존재)
//  - 브라우저가 푸시 지원
//  - 알림 권한이 아직 "default"(미결정) - 이미 허용/차단이면 물을 필요 없음
//  - 이 기기에서 이전에 물어본 적 없음(localStorage 플래그)
// 동의/거절 무관하게 한 번 물으면 다시 묻지 않는다(거절 시에도 마이페이지 토글로 언제든 켤 수 있음).

import { useEffect, useRef } from "react";
import { useConfirm } from "@oo/ui";
import { useAuthStore } from "@/stores/authStore.ts";
import { isNativeApp } from "@/shared/platform.ts";
import { getNotificationPermission, isPushSupported, subscribeUserPush } from "./pushClient.ts";
import { checkNativePushPermissions, enableNativePush } from "./nativePush.ts";

// 이미 한 번 물어봤는지 기록하는 기기 단위 플래그. 매 로그인마다 반복 노출 방지.
const PROMPT_FLAG = "oo-push-prompt-asked";

// 알림 권한이 아직 미결정(default/prompt)인지. 이미 허용/차단이면 물을 필요가 없다.
//  - 웹은 Notification API, 네이티브 앱은 FCM 플러그인 권한을 본다(WebView 엔 Notification API 가 없음).
async function isPermissionUndecided(): Promise<boolean> {
  if (isNativeApp) {
    try {
      const perm = await checkNativePushPermissions();
      return perm.receive === "prompt" || perm.receive === "prompt-with-rationale";
    } catch {
      return false;
    }
  }
  return getNotificationPermission() === "default";
}

// 렌더링 출력이 없는 동작 전용 컴포넌트. 레이아웃 어딘가에 한 번 마운트한다.
export default function PushConsentPrompt() {
  const confirm = useConfirm();
  const userId = useAuthStore((s) => s.session?.user.id ?? null);
  // StrictMode 이중 실행/리렌더에도 모달이 두 번 뜨지 않도록 1회 처리 가드.
  const handledRef = useRef(false);

  useEffect(() => {
    if (!userId) return; // 비로그인
    if (handledRef.current) return; // 이번 마운트에서 이미 처리
    // 네이티브 앱은 FCM 경로라 항상 지원. 웹은 웹 푸시 지원 브라우저만(안내는 마이페이지 토글이 담당).
    if (!isNativeApp && !isPushSupported()) return;
    if (localStorage.getItem(PROMPT_FLAG)) return; // 이전에 물어봄

    handledRef.current = true;

    void (async () => {
      if (!(await isPermissionUndecided())) return; // 이미 허용/차단됨 - 물을 필요 없음
      // 한 번만 묻도록 즉시 기록(동의/거절 결과와 무관).
      localStorage.setItem(PROMPT_FLAG, "1");
      const ok = await confirm({
        title: "주문 알림을 받으시겠어요?",
        description:
          "주문 접수·준비·완료 상태를 푸시 알림으로 바로 알려드립니다. 마이페이지에서 언제든 끄실 수 있어요.",
        confirmLabel: "알림 받기",
        cancelLabel: "다음에",
        tone: "brand",
      });
      if (ok) {
        // 결과 안내(권한 거부 등)는 마이페이지 토글에서 확인 가능하므로 여기서는 조용히 시도만 한다.
        if (isNativeApp) {
          await enableNativePush();
        } else {
          await subscribeUserPush();
        }
      }
    })();
  }, [userId, confirm]);

  return null;
}
